import {
  Component,
  OnInit,
  ViewChild,
  OnDestroy,
  AfterViewInit,
  TemplateRef,
  ChangeDetectorRef
} from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import * as FileSaver from 'file-saver';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { CustomTableComponent } from '../../../../../shared/ui/custom-table/components/table/custom-table.component';
import { TableConfig } from '../../../../../shared/ui/custom-table/model/table-config.model';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';

import { createLoanTableConfig, TableRowAction } from './loan-table.config';
import {
  LoanOperationKind,
  LoanRow,
  loanDurationLabel,
  loanDurationVariant,
  roundMoney,
  toLoanRow
} from '../../models/loan.model';
import { LoanOperationDialogComponent } from '../loan-operation-dialog/loan-operation-dialog.component';
import { LoanAttachmentDialogComponent } from '../loan-attachment-dialog/loan-attachment-dialog.component';
import { LoanFormDialogComponent } from '../loan-form-dialog/loan-form-dialog.component';

import { Utils } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { PrestamosService } from '../../../../../modules/finanzas/prestamos/prestamos.service';
import { LoanReadService } from '../../services/loan-read.service';
import { mapTableState, tableStateEquals } from '../../../../shared/utils/table-state.utils';
import { TableStateSnapshot } from '../../../../shared/utils/table-types';
import { AccountingManualFormComponent } from '../../../../../modules/accounting/manual/accounting-manual-form.component';

@Component({
  standalone: false,
  selector: 'app-loan-list',
  templateUrl: './loan-list.components.html',
  styleUrls: ['./loan-list.components.css']
})
export class LoanListComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('table', { static: false }) t1!: CustomTableComponent;
  @ViewChild('quickFilter') quickFilterTpl!: TemplateRef<any>;
  @ViewChild('loanSummary') loanSummaryTpl!: TemplateRef<any>;
  @ViewChild('expandDetail') expandDetailTpl!: TemplateRef<any>;

  tableConfig!: TableConfig;

  icon = 'account_balance';
  title = 'Préstamos bancarios';
  subTitle = 'Consulta, constituye y paga las cuotas de tus préstamos';

  totals = { initial: 0, pending: 0, amortized: 0, count: 0 };

  quickSearch = '';
  private readonly quickSearch$ = new Subject<string>();

  private rows: LoanRow[] = [];
  private lastState: TableStateSnapshot | null = null;
  private loadSeq = 0;

  private destroy$ = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private prestamosService: PrestamosService,
    private reads: LoanReadService,
    private notify: NotificationService,
    private confirm: ConfirmService,
    private tier: TierService,
    private loading: LoadingDialogService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    const money = (val: number) => Utils.FormatMoney(val ?? 0) + ' €';
    const date = (val: any) => val ? formatDate(val, 'dd/MM/yyyy', 'es-ES') : '';

    this.tableConfig = createLoanTableConfig(
      {
        actionMap: {
          [TableRowAction.Edit]: (row) => this.edit(row),
          [TableRowAction.Delete]: (row) => this.delete(row),
          [TableRowAction.UploadFile]: (row) => this.uploadFileAttachment(row),
          [TableRowAction.DownloadFile]: (row) => this.downloadFileAttachment(row),
          [TableRowAction.Constitution]: (row) => this.openOperation('constitution', row),
          [TableRowAction.ShortTermDebt]: (row) => this.openOperation('shortTermDebt', row),
          [TableRowAction.PayInstallments]: (row) => this.openOperation('installment', row),
        },
        selectable: false,
        columns: [
          { key: 'entidad', label: 'Entidad', sortable: true, sortKey: 'Entidad', ellipsis: true, onCellClick: (row: LoanRow) => this.edit(row) },
          { key: 'numero', label: 'Nº préstamo', sortable: true, sortKey: 'Numero' },
          {
            key: 'duracion', label: 'Duración', sortable: true, sortKey: 'Duracion',
            badge: {
              variantFn: (val: number) => loanDurationVariant(val),
              labelFn: (val: number) => loanDurationLabel(val),
            },
          },
          { key: 'importeInicial', label: 'Importe inicial', sortable: true, sortKey: 'ImporteInicial', formatFn: money },
          { key: 'importePendiente', label: 'Importe pendiente', sortable: true, sortKey: 'ImportePendiente', formatFn: money },
          {
            key: 'percent', label: 'Amortizado', sortable: true, sortKey: 'Percent',
            badge: {
              variantFn: (val: number, row: LoanRow) => this.amortizationVariant(row),
              labelFn: (val: number, row: LoanRow) => this.amortizationLabel(row),
            },
          },
          { key: 'fechaConstitucion', label: 'Fecha constitución', sortable: true, sortKey: 'FechaConstitucion', formatFn: date },
          { key: 'fechaFinalizacion', label: 'Fecha finalización', sortable: true, sortKey: 'FechaFinalizacion', formatFn: date },
        ],
        headerActions: [
          { label: '', icon: 'add', type: 'button', callback: () => this.create() },
        ],
        gridActions: [
          { label: 'Contabilizar (asiento libre)', icon: 'show_chart', type: 'button', callback: () => this.addManualAccountMovements() },
        ],
        rowConfig: {},
      },
      {
        filterView: '',
      }
    );

    this.tableConfig.rowConfig = {
      ...this.tableConfig.rowConfig,
      expandable: true,
      onExpand: () => { },
      onCollapse: () => { },
      onToggleExpand: () => { },
    };
  }

  ngAfterViewInit(): void {
    this.tableConfig.quickFilterTemplate = this.quickFilterTpl;
    this.tableConfig.summaryTemplate = this.loanSummaryTpl;
    this.tableConfig.expandableRowTemplate = this.expandDetailTpl;

    this.quickSearch$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(value => this.t1.tableState.patchFilters({ search: value }));

    this.t1.tableState.state$
      .pipe(
        map(mapTableState),
        distinctUntilChanged(tableStateEquals),
        takeUntil(this.destroy$),
      )
      .subscribe(state => {
        const search = String((state.filters || {}).search || '');
        if (search !== this.quickSearch) this.quickSearch = search;
        this.lastState = state;
        this.load();
      });

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(value: string): void {
    this.quickSearch = value || '';
    this.quickSearch$.next(this.quickSearch);
  }

  create(): void {
    if (!this.tier.requireAdvancedTier()) return;
    LoanFormDialogComponent.open(this.dialog, {})
      .subscribe(saved => { if (saved) this.reload(); });
  }

  edit(row: LoanRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    LoanFormDialogComponent.open(this.dialog, { uid: row.uid })
      .subscribe(saved => { if (saved) this.reload(); });
  }

  delete(row: LoanRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.confirm.open({
      title: 'Eliminar préstamo',
      subtitle: row.descripcion || undefined,
      message: 'Se eliminarán el préstamo, su constitución, las reclasificaciones a corto plazo y los pagos de cuotas junto con sus asientos contables. Las cuentas contables del préstamo se borrarán si no tienen movimientos. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.loanDetails(row),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando préstamo…',
      errorText: 'No se pudo eliminar el préstamo',
      action: () => this.prestamosService.delete(row.uid).then(ok => this.ensureOk(ok)),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Préstamo eliminado correctamente');
      this.load();
    });
  }

  uploadFileAttachment(row: LoanRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    LoanAttachmentDialogComponent.open(this.dialog, { loan: row })
      .subscribe(changed => { if (changed) this.reload(); });
  }

  downloadFileAttachment(row: LoanRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.loading.run('Descargando cuadro de amortización…', this.prestamosService.downloadFileAttachment(row.uid))
      .then(response => {
        FileSaver.saveAs(response, Utils.GetFileName('Fichero_Adjunto', consts.EXT_EXCEL));
        this.notify.ok('Cuadro de amortización descargado');
      })
      .catch(() => this.notify.error('No se pudo descargar el cuadro de amortización'));
  }

  openOperation(kind: LoanOperationKind, row: LoanRow): void {
    if (!this.tier.requireAdvancedTier()) return;
    LoanOperationDialogComponent.open(this.dialog, { kind, loan: row })
      .subscribe(changed => { if (changed) this.reload(); });
  }

  addManualAccountMovements(): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.dialog.open(AccountingManualFormComponent, {
      width: '90vw',
      maxWidth: '96vw',
      height: '70%',
      data: { accountingMovementId: '' }
    }).afterClosed().subscribe((result: string) => {
      if (!Utils.IsNullOrEmpty(result) && result === 'OK') {
        this.reload();
      }
    });
  }

  reload(): void {
    this.load();
  }

  private load(): void {
    if (!this.t1 || !this.lastState) return;
    const state = this.lastState;
    const filters: any = state.filters || {};
    const seq = ++this.loadSeq;
    this.t1.tableState.setLoading(true);

    this.reads.list({
      page: (state.pageIndex || 0) + 1,
      pageSize: state.pageSize || 10,
      search: String(filters.search || '').trim(),
      sortField: state.sortField || '',
      sortDirection: state.sortDirection || '',
    })
      .then(result => {
        if (seq !== this.loadSeq) return;
        const expanded = new Set(this.rows.filter(row => row.__expanded).map(row => row.uid));
        this.rows = result.items.map(item => toLoanRow(item, expanded.has(item.uid)));
        this.totals = {
          initial: roundMoney(result.summary.initialAmount),
          pending: roundMoney(result.summary.outstandingAmount),
          amortized: roundMoney(result.summary.amortizedAmount),
          count: result.total,
        };
        this.t1.tableState.setData(this.rows);
        this.t1.tableState.setTotalItems(result.total);
        this.t1.tableState.setLoading(false);
        this.cdr.detectChanges();
      })
      .catch(error => {
        if (seq !== this.loadSeq) return;
        this.t1.tableState.setLoading(false);
        this.notify.error((error && error.error && error.error.message) || 'No se pudo cargar la lista de préstamos');
      });
  }

  private amortizationVariant(row: LoanRow): 'success' | 'info' | 'warning' | 'neutral' {
    if (!row || !(Number(row.importeInicial) > 0)) return 'neutral';
    if (Number(row.importePendiente) <= 0) return 'success';
    return row.amortizado > 0 ? 'info' : 'warning';
  }

  private amortizationLabel(row: LoanRow): string {
    if (!row || !(Number(row.importeInicial) > 0)) return 'Sin importe';
    if (Number(row.importePendiente) <= 0) return 'Amortizado';
    return Utils.FormatMoney(row.percent, 0) + ' %';
  }

  private loanDetails(row: LoanRow): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (row.numero) details.push({ label: 'Nº préstamo', value: row.numero });
    details.push({ label: 'Duración', value: loanDurationLabel(row.duracion) });
    details.push({ label: 'Importe inicial', value: Utils.FormatMoney(row.importeInicial ?? 0) + ' €' });
    details.push({ label: 'Importe pendiente', value: Utils.FormatMoney(row.importePendiente ?? 0) + ' €' });
    return details;
  }

  private ensureOk(ok: unknown): void {
    if (ok !== true) throw new Error();
  }
}
